































































































































import { PopupPhone, PopupPhoneCallService } from '~/models/phone/types'
import CHybridTelephone from '~/components/shared/configurable/telephone/CHybridTelephone.vue'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import PhoneService from '~/services/PhoneService'
import { Seller } from '~/models/classified/types'
import {
  defineComponent,
  PropType,
  toRefs,
  computed,
  onMounted,
  onBeforeUnmount,
  ref
} from '~/utils/nuxt3-migration'
import { ciUser } from '~/icons/source/solid/user'
import { ciViber } from '~/icons/source/brand/viber'
import { ciWhatsApp } from '~/icons/source/brand/whats-app'
import { ciCall } from '~/icons/source/light/call'
import StatsService from '~/services/stats/StatsService'
import { useDeps } from '~/compositions/dependency-container'
import { useI18n } from '~/compositions/i18n'
import useTurnstile from '~/compositions/useTurnstile'
import { sellerIsOfTypeDealer } from '~/utils/user'
import { useNamespacedStore } from '~/compositions/store'
import { HttpStatus } from '~/constants/http'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import { SET_CONTACT_INFO } from '~/store/modules/shared/classifieds/view/mutation-types'
import { useLogger } from '~/compositions/logger'
import { useUserAgent } from '~/compositions/user-agent'
import SellerInfoIcon from '~/components/car/classifieds/view/partials/seller/dealer/SellerInfoIcon.vue'

export default defineComponent({
  components: {
    SellerInfoIcon,
    CFormError,
    CHybridTelephone
  },
  props: {
    seller: {
      type: Object as PropType<Seller>,
      required: true
    },
    withoutDimmer: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { seller } = toRefs(props)
    const { t } = useI18n()
    const [phoneService, statsService] = useDeps(PhoneService, StatsService)
    const { loadTurnstile, removeWidget } = useTurnstile()
    const turnstileRef = ref<HTMLElement | null>(null)
    const error = ref(null)
    const logger = useLogger()
    const { isMobile } = useUserAgent()

    const { state: viewState, commit: viewCommit } = useNamespacedStore<
      ClassifiedViewState
    >(CLASSIFIED_VIEW_NS)

    const classifiedCategory = computed(() => {
      return viewState.classified.category_ids.length >= 2
        ? viewState.classified.category_ids[1]
        : null
    })
    const sellerIsDealerOrAgent = computed(() =>
      sellerIsOfTypeDealer(seller.value)
    )

    const phones = computed<PopupPhone[]>(() => {
      const phones = []
      seller.value.telephone1 &&
        phones.push(
          phoneService.createPopupPhone(
            seller.value.telephone1,
            seller.value.name,
            t('telephone') + ' 1',
            'seller'
          )
        )
      seller.value.telephone2 &&
        phones.push(
          phoneService.createPopupPhone(
            seller.value.telephone2,
            seller.value.name,
            t('telephone') + ' 2',
            'seller'
          )
        )
      seller.value.mobile &&
        phones.push(
          phoneService.createPopupPhone(
            seller.value.mobile,
            seller.value.name,
            t('mobile') as string,
            'seller'
          )
        )
      seller.value.extra_telephone &&
        phones.push(
          phoneService.createPopupPhone(
            seller.value.extra_telephone,
            seller.value.extra_name as string,
            t('property manager') as string,
            'extra'
          )
        )
      const uniquePhones = [
        ...new Map(phones.map(item => [item.number.telephone, item])).values()
      ]
      return uniquePhones
    })

    const phonesGrouped = computed(() => {
      // phones grouped by type
      const reduced = phones.value.reduce(function(obj, item) {
        obj[item.type] = obj[item.type] || []
        obj[item.type].push(item)
        return obj
      }, {})

      return Object.keys(reduced).map(function(key) {
        return { type: key, phones: reduced[key] }
      })
    })

    const showInGroups = computed(() => {
      return Boolean(phonesGrouped.value.length > 1)
    })

    const handleCallServiceButtonClick = (url: string) => {
      window.location.href = url
      const scheme = url.substring(0, url.indexOf(':'))
      const statSuffix = scheme === 'tel' ? '' : '.' + scheme
      statsService.record('events.clsfds.telephone_click' + statSuffix, [
        viewState.classifiedId
      ])
    }

    const handleUrlButtonClick = (service: PopupPhoneCallService) => {
      if (!service) {
        return
      }

      handleCallServiceButtonClick(service.url)
    }

    const getGroupName = (phoneGroup: any) => {
      if (phoneGroup.type === 'extra') {
        return t('property manager')
      }

      return t('seller')
    }

    const onTurnstileCallback = async (token: string) => {
      try {
        const details = await phoneService.getContactInfo(
          viewState.classifiedId,
          classifiedCategory.value,
          seller.value.cdt,
          token
        )

        viewCommit(SET_CONTACT_INFO, details)
      } catch (e) {
        if (e?.response?.status === HttpStatus.TOO_MANY_REQUESTS) {
          error.value = `${t(
            'you performed too many requests in a given amount of time'
          )}. ${t('please try again later')}`
        } else {
          error.value = t('an error occurred please try again later')
        }

        logger.captureError(error)
      }
    }

    const onTurnstileError = (tsError: any) => {
      error.value = t('an error occurred please try again later')
      logger.captureError(tsError)
    }

    onMounted(() => {
      if (phonesGrouped.value.length === 0) {
        loadTurnstile(
          turnstileRef.value,
          onTurnstileCallback,
          'contact-info',
          onTurnstileError
        )
      }
    })

    onBeforeUnmount(() => {
      removeWidget()
    })

    const icons = computed(() => {
      return {
        phone: ciCall,
        viber: ciViber,
        whatsapp: ciWhatsApp,
        user: ciUser
      }
    })

    const sizeToUse = computed(() => {
      return showInGroups.value && isMobile.value ? 'md' : 'lg'
    })

    return {
      ciViber,
      ciWhatsApp,
      ciUser,
      ciCall,
      phones,
      phonesGrouped,
      showInGroups,
      sellerIsDealerOrAgent,
      handleUrlButtonClick,
      getGroupName,
      turnstileRef,
      error,
      icons,
      sizeToUse
    }
  }
})
